import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const IntegrationsTab = () => {
  const [integrations, setIntegrations] = useState([
    {
      id: 1,
      name: 'CRM Entegrasyonu',
      type: 'crm',
      provider: 'SalesForce',
      status: 'connected',
      lastSync: '2025-11-07 15:30',
      apiEndpoint: 'https://api.salesforce.com/v1',
      features: ['Müşteri Bilgileri', 'Arama Geçmişi', 'Otomatik Kayıt'],
      icon: 'Database'
    },
    {
      id: 2,
      name: 'E-posta Entegrasyonu',
      type: 'email',
      provider: 'Microsoft Outlook',
      status: 'connected',
      lastSync: '2025-11-07 14:45',
      apiEndpoint: 'https://graph.microsoft.com/v1.0',
      features: ['E-posta Gönderimi', 'Takvim Entegrasyonu'],
      icon: 'Mail'
    },
    {
      id: 3,
      name: 'SMS Servisi',
      type: 'sms',
      provider: 'Twilio',
      status: 'disconnected',
      lastSync: 'Hiçbir zaman',
      apiEndpoint: 'https://api.twilio.com/2010-04-01',
      features: ['SMS Gönderimi', 'Toplu Mesaj'],
      icon: 'MessageSquare'
    },
    {
      id: 4,
      name: 'Muhasebe Sistemi',
      type: 'accounting',
      provider: 'Logo Go',
      status: 'error',
      lastSync: '2025-11-06 09:15',
      apiEndpoint: 'https://api.logo.com.tr/v2',
      features: ['Fatura Entegrasyonu', 'Müşteri Bakiyeleri'],
      icon: 'Calculator'
    }
  ]);

  const [showAddIntegrationModal, setShowAddIntegrationModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);

  const [newIntegration, setNewIntegration] = useState({
    name: '',
    type: 'crm',
    provider: '',
    apiEndpoint: '',
    apiKey: '',
    apiSecret: '',
    webhookUrl: '',
    enableWebhooks: false,
    syncInterval: 15,
    features: []
  });

  const integrationTypes = [
    { value: 'crm', label: 'CRM Sistemi' },
    { value: 'email', label: 'E-posta Servisi' },
    { value: 'sms', label: 'SMS Servisi' },
    { value: 'accounting', label: 'Muhasebe Sistemi' },
    { value: 'helpdesk', label: 'Yardım Masası' },
    { value: 'analytics', label: 'Analitik Servisi' }
  ];

  const syncIntervalOptions = [
    { value: 5, label: '5 dakika' },
    { value: 15, label: '15 dakika' },
    { value: 30, label: '30 dakika' },
    { value: 60, label: '1 saat' },
    { value: 240, label: '4 saat' },
    { value: 1440, label: '24 saat' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'bg-success/10 text-success border-success/20';
      case 'disconnected': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'error': return 'bg-error/10 text-error border-error/20';
      case 'syncing': return 'bg-warning/10 text-warning border-warning/20';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'connected': return 'Bağlı';
      case 'disconnected': return 'Bağlantısız';
      case 'error': return 'Hata';
      case 'syncing': return 'Senkronize Ediliyor';
      default: return status;
    }
  };

  const handleConnect = (integrationId) => {
    setIntegrations(integrations?.map(integration => 
      integration?.id === integrationId 
        ? { ...integration, status: 'connected', lastSync: new Date()?.toLocaleString('tr-TR') }
        : integration
    ));
  };

  const handleDisconnect = (integrationId) => {
    setIntegrations(integrations?.map(integration => 
      integration?.id === integrationId 
        ? { ...integration, status: 'disconnected' }
        : integration
    ));
  };

  const handleSync = (integrationId) => {
    setIntegrations(integrations?.map(integration => 
      integration?.id === integrationId 
        ? { ...integration, status: 'syncing' }
        : integration
    ));
    
    // Simulate sync process
    setTimeout(() => {
      setIntegrations(prev => prev?.map(integration => 
        integration?.id === integrationId 
          ? { ...integration, status: 'connected', lastSync: new Date()?.toLocaleString('tr-TR') }
          : integration
      ));
    }, 3000);
  };

  const handleAddIntegration = () => {
    const integration = {
      id: integrations?.length + 1,
      ...newIntegration,
      status: 'disconnected',
      lastSync: 'Hiçbir zaman',
      icon: 'Plug'
    };
    setIntegrations([...integrations, integration]);
    setNewIntegration({
      name: '',
      type: 'crm',
      provider: '',
      apiEndpoint: '',
      apiKey: '',
      apiSecret: '',
      webhookUrl: '',
      enableWebhooks: false,
      syncInterval: 15,
      features: []
    });
    setShowAddIntegrationModal(false);
  };

  const openConfigModal = (integration) => {
    setSelectedIntegration(integration);
    setShowConfigModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Sistem Entegrasyonları</h3>
          <p className="text-sm text-slate-600 mt-1">Harici sistemlerle entegrasyon ayarlarını yönetin</p>
        </div>
        <Button
          variant="default"
          onClick={() => setShowAddIntegrationModal(true)}
          iconName="Plus"
          iconPosition="left"
        >
          Yeni Entegrasyon
        </Button>
      </div>
      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {integrations?.map((integration) => (
          <div key={integration?.id} className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={integration?.icon} size={20} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">{integration?.name}</h4>
                  <p className="text-sm text-slate-600">{integration?.provider}</p>
                </div>
              </div>
              <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(integration?.status)}`}>
                {getStatusLabel(integration?.status)}
              </span>
            </div>

            <div className="space-y-3 mb-4">
              <div className="text-sm">
                <span className="text-slate-600">Son Senkronizasyon:</span>
                <div className="font-medium text-slate-900">{integration?.lastSync}</div>
              </div>
              
              <div className="text-sm">
                <span className="text-slate-600">Özellikler:</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {integration?.features?.map((feature, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {integration?.status === 'connected' ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSync(integration?.id)}
                    loading={integration?.status === 'syncing'}
                    iconName="RefreshCw"
                    className="flex-1"
                  >
                    Senkronize Et
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openConfigModal(integration)}
                    iconName="Settings"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDisconnect(integration?.id)}
                    iconName="Unlink"
                  />
                </>
              ) : (
                <>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleConnect(integration?.id)}
                    iconName="Link"
                    className="flex-1"
                  >
                    Bağlan
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openConfigModal(integration)}
                    iconName="Settings"
                  />
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Add Integration Modal */}
      {showAddIntegrationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Yeni Entegrasyon Ekle</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddIntegrationModal(false)}
                  iconName="X"
                />
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Entegrasyon Adı"
                  type="text"
                  value={newIntegration?.name}
                  onChange={(e) => setNewIntegration({...newIntegration, name: e?.target?.value})}
                  placeholder="CRM Entegrasyonu"
                  required
                />
                <Select
                  label="Entegrasyon Tipi"
                  options={integrationTypes}
                  value={newIntegration?.type}
                  onChange={(value) => setNewIntegration({...newIntegration, type: value})}
                />
                <Input
                  label="Sağlayıcı"
                  type="text"
                  value={newIntegration?.provider}
                  onChange={(e) => setNewIntegration({...newIntegration, provider: e?.target?.value})}
                  placeholder="SalesForce"
                  required
                />
                <Input
                  label="API Endpoint"
                  type="url"
                  value={newIntegration?.apiEndpoint}
                  onChange={(e) => setNewIntegration({...newIntegration, apiEndpoint: e?.target?.value})}
                  placeholder="https://api.example.com/v1"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="API Anahtarı"
                  type="password"
                  value={newIntegration?.apiKey}
                  onChange={(e) => setNewIntegration({...newIntegration, apiKey: e?.target?.value})}
                  placeholder="API Key"
                  required
                />
                <Input
                  label="API Secret"
                  type="password"
                  value={newIntegration?.apiSecret}
                  onChange={(e) => setNewIntegration({...newIntegration, apiSecret: e?.target?.value})}
                  placeholder="API Secret"
                />
              </div>

              <div>
                <Checkbox
                  label="Webhook'ları Etkinleştir"
                  description="Gerçek zamanlı veri senkronizasyonu için webhook'ları kullan"
                  checked={newIntegration?.enableWebhooks}
                  onChange={(e) => setNewIntegration({...newIntegration, enableWebhooks: e?.target?.checked})}
                />
                
                {newIntegration?.enableWebhooks && (
                  <div className="mt-4">
                    <Input
                      label="Webhook URL"
                      type="url"
                      value={newIntegration?.webhookUrl}
                      onChange={(e) => setNewIntegration({...newIntegration, webhookUrl: e?.target?.value})}
                      placeholder="https://your-domain.com/webhook"
                    />
                  </div>
                )}
              </div>

              <Select
                label="Senkronizasyon Aralığı"
                options={syncIntervalOptions}
                value={newIntegration?.syncInterval}
                onChange={(value) => setNewIntegration({...newIntegration, syncInterval: value})}
                description="Otomatik veri senkronizasyon sıklığı"
              />
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-end space-x-4">
              <Button
                variant="outline"
                onClick={() => setShowAddIntegrationModal(false)}
              >
                İptal
              </Button>
              <Button
                variant="default"
                onClick={handleAddIntegration}
                iconName="Plus"
                iconPosition="left"
              >
                Entegrasyon Ekle
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Configuration Modal */}
      {showConfigModal && selectedIntegration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">
                  {selectedIntegration?.name} - Yapılandırma
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowConfigModal(false)}
                  iconName="X"
                />
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-2">Bağlantı Bilgileri</h4>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium">API Endpoint:</span> {selectedIntegration?.apiEndpoint}</div>
                  <div><span className="font-medium">Durum:</span> {getStatusLabel(selectedIntegration?.status)}</div>
                  <div><span className="font-medium">Son Senkronizasyon:</span> {selectedIntegration?.lastSync}</div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-slate-900 mb-4">Test Bağlantısı</h4>
                <Button
                  variant="outline"
                  iconName="Zap"
                  iconPosition="left"
                >
                  Bağlantıyı Test Et
                </Button>
              </div>

              <div>
                <h4 className="font-medium text-slate-900 mb-4">Senkronizasyon Ayarları</h4>
                <div className="space-y-4">
                  <Select
                    label="Senkronizasyon Aralığı"
                    options={syncIntervalOptions}
                    value={15}
                    onChange={() => {}}
                  />
                  <Checkbox
                    label="Otomatik Senkronizasyon"
                    description="Belirtilen aralıklarla otomatik olarak senkronize et"
                    checked
                    onChange={() => {}}
                  />
                  <Checkbox
                    label="Hata Durumunda E-posta Gönder"
                    description="Senkronizasyon hatalarında yöneticilere e-posta gönder"
                   
                    onChange={() => {}}
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-end space-x-4">
              <Button
                variant="outline"
                onClick={() => setShowConfigModal(false)}
              >
                İptal
              </Button>
              <Button
                variant="default"
                iconName="Save"
                iconPosition="left"
              >
                Ayarları Kaydet
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsTab;